import retry from 'retry'

import { RETRY_OPTIONS } from '@disco/api/server/constants'

/**
 * Configuration options for retry behavior.
 */
type RetryOptions = {
  /** Maximum number of retry attempts (default from RETRY_OPTIONS) */
  retries?: number
  /** Exponential backoff factor (default from RETRY_OPTIONS) */
  factor?: number
  /** Minimum timeout between retries in milliseconds (default from RETRY_OPTIONS) */
  minTimeout?: number
  /** Maximum timeout between retries in milliseconds (default from RETRY_OPTIONS) */
  maxTimeout?: number
  /** Whether to randomize retry timeouts (default from RETRY_OPTIONS) */
  randomize?: boolean
}

/**
 * Retries a promise-returning function with configurable retry logic and abort signal support.
 *
 * Uses exponential backoff for retry delays and can be aborted using an AbortSignal.
 * Merges provided options with default RETRY_OPTIONS.
 *
 * @template T - The type of the resolved promise value
 * @param promise - Function that returns a promise to be retried
 * @param abortSignal - Optional AbortSignal to cancel the retry operation
 * @param options - Optional retry configuration that overrides defaults
 * @returns Promise that resolves with the successful result or rejects with the final error
 *
 * @example
 * ```typescript
 * // Basic retry
 * const result = await retryPromise(() => fetch('/api/data'))
 *
 * // With custom options
 * const result = await retryPromise(
 *   () => fetch('/api/data'),
 *   undefined,
 *   { retries: 3, minTimeout: 1000 }
 * )
 *
 * // With abort signal
 * const controller = new AbortController()
 * const result = await retryPromise(
 *   () => fetch('/api/data'),
 *   controller.signal
 * )
 * // Later: controller.abort()
 * ```
 */
export const retryPromise = <T>(
  promise: () => Promise<T>,
  abortSignal?: AbortSignal,
  options?: RetryOptions,
): Promise<T> => {
  if (abortSignal?.aborted) return Promise.reject(new Error('Aborted'))

  return new Promise(
    (
      resolve: (result: Promise<T> | T) => void,
      reject: (error?: any) => void,
    ) => {
      abortSignal?.addEventListener('abort', () => {
        reject(new Error('Aborted'))
      })

      const operation = retry.operation({ ...RETRY_OPTIONS, ...options })

      operation.attempt(async () => {
        try {
          const result = await promise()
          resolve(result)
        } catch (err: any) {
          if (operation.retry(err)) {
            return
          }
          reject(operation.mainError())
        }
      })
    },
  )
}
